import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "../lib/api";
import { Plus, X, Building2 as Buildings } from "lucide-react";
import { toast } from "sonner";

const COLORS = ["#002FA7", "#0A0A0B", "#059669", "#E11D48", "#F59E0B", "#7C3AED"];

export default function Brands() {
  const [brands, setBrands] = useState([]);
  const [showAdd, setShowAdd] = useState(false);
  const [form, setForm] = useState({ name: "", is_client: false, description: "", sells: "", voice: "", color: COLORS[0], substack_url: "", playbooks: [] });

  const load = () => api.listBrands().then(setBrands);
  useEffect(() => { load(); }, []);

  const submit = async () => {
    if (!form.name.trim()) return toast.error("Name required");
    try {
      await api.createBrand(form);
      setShowAdd(false);
      setForm({ name: "", is_client: false, description: "", sells: "", voice: "", color: COLORS[0], substack_url: "", playbooks: [] });
      load();
      toast.success("Brand created");
    } catch (e) {
      toast.error("Create failed");
    }
  };

  return (
    <div data-testid="brands-page" className="max-w-7xl">
      <div className="flex items-end justify-between mb-10">
        <div>
          <div className="text-eyebrow mb-3">Roster</div>
          <h1 className="font-display font-black text-5xl md:text-6xl tracking-tighter">Brands</h1>
          <p className="text-[#52525B] mt-3">Add your own brands and client brands. Connect them for cross-pollination.</p>
        </div>
        <button
          onClick={() => setShowAdd(true)}
          data-testid="add-brand-button"
          className="bg-[#0A0A0B] text-white px-5 py-3 text-sm flex items-center gap-2 hover:bg-[#002FA7] transition-colors"
        >
          <Plus size={16} /> Add Brand
        </button>
      </div>

      {brands.length === 0 ? (
        <div className="border-2 border-dashed border-[#E2E4E9] p-16 text-center">
          <Buildings size={48} className="mx-auto text-[#A1A1AA]" />
          <p className="font-display font-bold text-2xl mt-4">Start with your first brand</p>
          <p className="text-sm text-[#52525B] mt-2">We'll interview it and start generating content within minutes.</p>
        </div>
      ) : (
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-0 border-l border-t border-[#E2E4E9]">
          {brands.map((b) => (
            <Link
              key={b.brand_id}
              to={`/brands/${b.brand_id}`}
              data-testid={`brand-card-${b.brand_id}`}
              className="p-6 border-r border-b border-[#E2E4E9] hover:bg-[#F4F5F6] transition-colors"
            >
              <div className="flex items-center justify-between mb-4">
                <div className="w-12 h-12" style={{ background: b.color }} />
                <span className={`text-eyebrow ${b.is_client ? "text-[#002FA7]" : "text-[#059669]"}`}>
                  {b.is_client ? "Client" : "Owned"}
                </span>
              </div>
              <div className="font-display font-bold text-2xl tracking-tight">{b.name}</div>
              <p className="text-sm text-[#52525B] mt-2 line-clamp-2">{b.sells || b.description || "—"}</p>
              <div className="mt-4 flex items-center justify-between mono text-xs text-[#A1A1AA]">
                <span>knowledge {b.knowledge_score}/100</span>
                <span>{b.posts_generated} posts</span>
              </div>
              <div className="h-1 bg-[#F4F5F6] mt-3 relative">
                <div
                  className="absolute inset-y-0 left-0 bg-[#0A0A0B]"
                  style={{ width: `${Math.min(100, b.knowledge_score)}%` }}
                />
              </div>
            </Link>
          ))}
        </div>
      )}

      {showAdd && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={() => setShowAdd(false)}>
          <div
            className="bg-white border-2 border-black w-full max-w-xl p-8 brutal-shadow-lg"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between mb-6">
              <h2 className="font-display font-bold text-3xl tracking-tight">New Brand</h2>
              <button onClick={() => setShowAdd(false)} data-testid="close-add-brand"><X size={20} /></button>
            </div>
            <div className="space-y-4">
              <Field label="Brand name" required>
                <input data-testid="brand-name-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="w-full border border-[#E2E4E9] focus:border-black p-3 outline-none" />
              </Field>
              <Field label="What does it sell?">
                <input data-testid="brand-sells-input" value={form.sells} onChange={(e) => setForm({ ...form, sells: e.target.value })} placeholder="e.g. ghostwriting services at $5k/mo" className="w-full border border-[#E2E4E9] focus:border-black p-3 outline-none" />
              </Field>
              <Field label="Short description">
                <textarea data-testid="brand-description-input" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} rows={3} className="w-full border border-[#E2E4E9] focus:border-black p-3 outline-none resize-none" />
              </Field>
              <Field label="Brand voice (one line)">
                <input data-testid="brand-voice-input" value={form.voice} onChange={(e) => setForm({ ...form, voice: e.target.value })} placeholder="punchy, direct, anti-fluff" className="w-full border border-[#E2E4E9] focus:border-black p-3 outline-none" />
              </Field>
              <Field label="Type">
                <label className="flex items-center gap-2 text-sm">
                  <input type="checkbox" data-testid="brand-is-client-toggle" checked={form.is_client} onChange={(e) => setForm({ ...form, is_client: e.target.checked })} />
                  Mark as client brand
                </label>
              </Field>
              <Field label="Color">
                <div className="flex gap-2">
                  {COLORS.map((c) => (
                    <button
                      key={c}
                      onClick={() => setForm({ ...form, color: c })}
                      className={`w-8 h-8 border-2 ${form.color === c ? "border-black" : "border-transparent"}`}
                      style={{ background: c }}
                      data-testid={`color-${c}`}
                    />
                  ))}
                </div>
              </Field>
            </div>
            <div className="mt-8 flex gap-3 justify-end">
              <button onClick={() => setShowAdd(false)} className="px-5 py-3 text-sm border border-black hover:bg-[#F4F5F6]">Cancel</button>
              <button onClick={submit} data-testid="submit-brand-button" className="px-5 py-3 text-sm bg-[#0A0A0B] text-white hover:bg-[#002FA7]">Create</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function Field({ label, children, required }) {
  return (
    <div>
      <label className="text-eyebrow block mb-2">
        {label} {required && <span className="text-[#E11D48]">*</span>}
      </label>
      {children}
    </div>
  );
}
